<template>
  <div class="dialog">
    <span class="cls" @click="close"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <h2>{{ isEdit ? '청구사례 수정' : '청구사례 작성' }}</h2>
        <span class="close" @click="close">
          <i class="ph ph-x"></i>
        </span>
      </div>

      <!-- Form -->
      <div class="form-content">
        <!-- 카테고리 선택 -->
        <div class="category-select">
          <p class="section-label">카테고리</p>
          <div class="category-options">
            <button 
              v-for="cat in categoryOptions" 
              :key="cat.value"
              :class="['cat-btn', { active: form.category === cat.value }]"
              @click="form.category = cat.value"
            >
              {{ cat.label }}
            </button>
          </div>
        </div>

        <!-- 제목 -->
        <input 
          type="text" 
          class="title-input"
          v-model="form.title" 
          placeholder="사례 제목을 입력하세요"
        />

        <!-- 내용 -->
        <textarea 
          class="content-input"
          v-model="form.content" 
          placeholder="이번 청구사례를 자유롭게 적어주세요.

— 어떤 상황이었는지
— 진행하면서 느낀점
— 기억에 남는 순간"
        ></textarea>

        <!-- 이미지 추가 -->
        <div class="image-section">
          <p class="section-label">이미지 추가 <span class="hint">(여러 장 가능)</span></p>
          
          <div class="image-list">
            <!-- 추가된 이미지들 -->
            <div 
              class="image-item" 
              v-for="(img, idx) in form.images" 
              :key="idx"
            >
              <img :src="img" alt="첨부 이미지" />
              <button class="remove-btn" @click="removeImage(idx)">
                <i class="ph ph-x"></i>
              </button>
            </div>

            <!-- 이미지 추가 버튼 -->
            <div class="image-add" @click="triggerFileInput" :class="{ uploading: uploading }">
              <i class="ph" :class="uploading ? 'ph-spinner' : 'ph-plus'"></i>
              <p v-if="!uploading">개인정보는 블러처리 하<br/>고 올려주세요!</p>
              <p v-else>업로드 중...</p>
            </div>
          </div>

          <input 
            type="file" 
            ref="fileInput"
            accept="image/*"
            multiple
            @change="handleFileSelect"
            style="display: none;"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="footer">
        <button class="btn-cancel" @click="close">취소</button>
        <button class="btn-submit" @click="submit" :disabled="!isValid || submitting">
          {{ submitting ? '처리중...' : (isEdit ? '수정하기' : '등록하기') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, computed, onMounted } from "vue"

export default {
  props: {
    oth_showClaimExEdit: Function,
    editIdx: { default: null }
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')
    const mb_id = member_info.mb_id

    const fileInput = ref(null)
    const uploading = ref(false)
    const submitting = ref(false)
    const isEdit = computed(() => props.editIdx !== null)

    const categoryOptions = [
      { label: '암', value: 'cancer' },
      { label: '자동차', value: 'car' },
      { label: '3대질병', value: 'major3' },
      { label: '실손', value: 'actual' },
      { label: '기타', value: 'etc' }
    ]

    const form = ref({
      title: '',
      content: '',
      category: 'etc',
      images: []
    })

    const isValid = computed(() => {
      return form.value.title.trim() && form.value.content.trim()
    })

    // SQL 문자열 이스케이프
    const escapeSQL = (str) => {
      if (!str) return ''
      return str.replace(/\\/g, '\\\\').replace(/'/g, "''")
    }

    // 수정 시 기존 데이터 불러오기
    const loadPost = async () => {
      if (!props.editIdx) return
      
      try {
        const rows = await sql_query(`
          SELECT title, content, category, images
          FROM claim_ex_post
          WHERE idx = ${props.editIdx} AND mb_id = '${escapeSQL(mb_id)}'
        `)
        
        if (rows && rows.length > 0) {
          form.value = {
            title: rows[0].title || '',
            content: rows[0].content || '',
            category: rows[0].category || 'etc',
            images: rows[0].images ? JSON.parse(rows[0].images) : []
          }
        }
      } catch (e) {
        console.error('게시글 로드 오류:', e)
      }
    }

    // 파일 선택 트리거
    const triggerFileInput = () => {
      if (uploading.value) return
      fileInput.value.click()
    }

    // 파일 선택 처리 (서버 업로드)
    const handleFileSelect = async (e) => {
      const files = e.target.files
      if (!files.length) return

      uploading.value = true

      for (const file of files) {
        // // 파일 크기 체크 (5MB)
        // if (file.size > 5 * 1024 * 1024) {
        //   alert('파일 크기는 5MB 이하만 가능합니다.')
        //   continue
        // }

        const formData = new FormData()
        formData.append('idx', props.editIdx || '')
        formData.append('table', 'claim_ex_post')
        formData.append('usage', 'image')
        formData.append('user', mb_id)
        formData.append('file', file)

        try {
          const response = await fetch('/api/upload/image', {
            method: 'POST',
            body: formData,
            mode: 'cors',
            credentials: 'include'
          })
          const data = await response.json()

          if (data.url) {
            let imageUrl = data.url
            if (imageUrl.startsWith('/')) {
              imageUrl = window.location.origin + imageUrl
            }
            form.value.images.push(imageUrl)
          } else {
            alert('이미지 업로드 실패')
          }
        } catch (err) {
          console.error('업로드 오류:', err)
          alert('업로드 중 오류 발생')
        }
      }

      uploading.value = false
      e.target.value = ''
    }

    // 이미지 제거
    const removeImage = (idx) => {
      form.value.images.splice(idx, 1)
    }

    // 등록/수정
    const submit = async () => {
      if (!isValid.value || submitting.value) return

      submitting.value = true

      try {
        const imagesJson = form.value.images.length > 0 
          ? JSON.stringify(form.value.images) 
          : null

        if (isEdit.value) {
          await sql_query(`
            UPDATE claim_ex_post SET
              title = '${escapeSQL(form.value.title)}',
              content = '${escapeSQL(form.value.content)}',
              category = '${escapeSQL(form.value.category)}',
              images = ${imagesJson ? `'${escapeSQL(imagesJson)}'` : 'NULL'}
            WHERE idx = ${props.editIdx} AND mb_id = '${escapeSQL(mb_id)}'
          `)
        } else {
          await sql_query(`
            INSERT INTO claim_ex_post (mb_id, title, content, category, images)
            VALUES (
              '${escapeSQL(mb_id)}',
              '${escapeSQL(form.value.title)}',
              '${escapeSQL(form.value.content)}',
              '${escapeSQL(form.value.category)}',
              ${imagesJson ? `'${escapeSQL(imagesJson)}'` : 'NULL'}
            )
          `)
        }

        props.oth_showClaimExEdit(false, { refresh: true })
      } catch (e) {
        console.error('저장 오류:', e)
        alert('저장에 실패했습니다.')
      } finally {
        submitting.value = false
      }
    }

    const close = () => {
      props.oth_showClaimExEdit(false)
    }

    onMounted(() => {
      loadPost()
    })

    return {
      fileInput,
      uploading,
      submitting,
      isEdit,
      categoryOptions,
      form,
      isValid,
      triggerFileInput,
      handleFileSelect,
      removeImage,
      submit,
      close
    }
  }
}
</script>

<style scoped>
.dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  /* View 다이얼로그 위에 표시되도록 z-index 높임 */
  z-index: calc(var(--z-index-top) + 10);
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  width: 90%;
  max-width: 600px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  position: relative;
  flex-shrink: 0;
  border-bottom: 1px solid var(--color-background-light);
}

.header > h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.header > .close {
  position: absolute;
  right: 16px;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.header > .close > i {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

/* Form */
.form-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Category Select */
.category-select {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0;
}

.section-label > .hint {
  font-weight: var(--font-weight-normal);
  color: var(--color-text-secondary);
}

.category-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cat-btn {
  padding: 8px 16px;
  border: 1px solid var(--color-background-light);
  border-radius: 20px;
  background: var(--color-background);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.cat-btn.active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: var(--color-text-white);
}

.title-input {
  width: 100%;
  padding: 16px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  background: var(--color-background);
  box-sizing: border-box;
}

.title-input:focus {
  outline: none;
  border-color: var(--color-brand);
}

.title-input::placeholder {
  color: var(--color-text-third);
}

.content-input {
  width: 100%;
  min-height: 200px;
  padding: 16px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  background: var(--color-background);
  box-sizing: border-box;
  resize: none;
  line-height: 1.6;
}

.content-input:focus {
  outline: none;
  border-color: var(--color-brand);
}

.content-input::placeholder {
  color: var(--color-text-third);
}

/* Image Section */
.image-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.image-list {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.image-item {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: var(--radius-md);
  overflow: hidden;
}

.image-item > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-item > .remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-item > .remove-btn > i {
  font-size: 14px;
  color: #fff;
}

.image-add {
  width: 100px;
  height: 100px;
  border: 2px dashed var(--color-background-light);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.image-add:hover {
  border-color: var(--color-brand);
}

.image-add.uploading {
  pointer-events: none;
  opacity: 0.7;
}

.image-add.uploading > i {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.image-add > i {
  font-size: 24px;
  color: var(--color-text-secondary);
}

.image-add > p {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  text-align: center;
  margin: 0;
  line-height: 1.4;
}

/* Footer */
.footer {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.footer button {
  flex: 1;
  padding: 14px;
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  color: var(--color-text-secondary);
}

.btn-submit {
  background: var(--color-brand);
  border: none;
  color: var(--color-text-white);
}

.btn-submit:disabled {
  background: var(--color-background-light);
  cursor: not-allowed;
}
</style>